import type { IDashboardTemplate } from "../types";
import { EFamilyTypes } from "../components/DashboardTemplatesTypes";
import { AssetsService } from "../../../../services/AssetsService";
import { BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { translate } from "app/filters/i18nFilter";

const FAMILY = "Website";

const getDashboard = (): IDashboardTemplate => ({
    id: 17,
    title: "dashboard.templates.searchtrafficoverview.title",
    description: "dashboard.templates.searchtrafficoverview.description",
    descriptionLong: "dashboard.templates.searchtrafficoverview.description.long",
    keyMetrics: "dashboard.templates.searchtrafficoverview.keymetric",
    previewImage: AssetsService.assetUrl("/images/dashboard/templates/SearchTrafficOverview-preview.png"),
    thumbnailImage: AssetsService.assetUrl("/images/dashboard/templates/SearchTrafficOverview-thumb.png"),
    familyType: EFamilyTypes.Website,
    autoFillUserDomain: true,
    minItems: 1,
    maxItems: 5,
    isNew: true,
    widgets: [
        {
            pos: {
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                sizeX: 4,
                row: 0,
                col: 0,
            },
            properties: {
                flexibleDuration: true,
                selectedChannel: "Direct",
                metric: "EngagementVisits",
                webSource: devicesTypes.TOTAL,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                type: "EngagedVisitsSupportGraph",
                filters: {
                    timeGranularity: "Monthly",
                    includeSubDomains: "true",
                    ShouldGetVerifiedData: "true",
                    unBounced: "false",
                },
                width: "2",
                customAsset: false,
                family: FAMILY,
                options: {},
                title: translate("dashboard.templates.searchtrafficoverview.widget.monthlyvisits.title"),
            },
        },
        {
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 1,
                col: 0,
            },
            properties: {
                flexibleDuration: true,
                selectedChannel: "Organic Search",
                metric: "ChannelsAnalysisByTrafficShare",
                webSource: devicesTypes.TOTAL,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                type: "MmxTrafficShareDashboard",
                filters: {
                    includeSubDomains: "true",
                    timeGranularity: GranularityNames.MONTHLY,
                },
                width: "4",
                customAsset: false,
                options: {},
                family: "Website",
                title: translate("dashboard.templates.searchtrafficoverview.widget.organicsearch.title"),
            },
        },
        {
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 1,
                col: 2,
            },
            properties: {
                flexibleDuration: true,
                selectedChannel: "Paid Search",
                metric: "ChannelsAnalysisByTrafficShare",
                webSource: devicesTypes.TOTAL,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                type: "MmxTrafficShareDashboard",
                filters: {
                    includeSubDomains: "true",
                    timeGranularity: GranularityNames.MONTHLY,
                },
                width: "4",
                customAsset: false,
                options: {},
                family: "Website",
                title: translate("dashboard.templates.searchtrafficoverview.widget.paidsearch.title"),
            },
        },
        {
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 2,
                col: 0,
            },
            properties: {
                selectedChannel: "Organic Search",
                metric: "ChannelsAnalysisByTrafficShare",
                webSource: devicesTypes.DESKTOP,
                duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                type: "MmxTrafficShareDashboard",
                filters: {
                    includeSubDomains: "true",
                    timeGranularity: GranularityNames.DAILY,
                },
                width: "4",
                customAsset: false,
                options: {},
                family: "Website",
                title: translate("dashboard.templates.searchtrafficoverview.widget.organicsearch.desktop.daily.title"),
            },
        },
        {
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 2,
                col: 2,
            },
            properties: {
                selectedChannel: "Paid Search",
                metric: "ChannelsAnalysisByTrafficShare",
                webSource: devicesTypes.DESKTOP,
                duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                type: "MmxTrafficShareDashboard",
                filters: {
                    includeSubDomains: "true",
                    timeGranularity: GranularityNames.DAILY,
                },
                width: "4",
                customAsset: false,
                options: {},
                family: "Website",
                title: translate("dashboard.templates.searchtrafficoverview.widget.paidsearch.desktop.daily.title"),
            },
        },
        {
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 3,
                col: 0,
            },
            properties: {
                flexibleDuration: true,
                selectedChannel: "Paid Search",
                metric: "AdSpend",
                webSource: devicesTypes.DESKTOP,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                type: "AdSpendLineChart",
                filters: {
                    includeSubDomains: "true",
                    timeGranularity: GranularityNames.MONTHLY,
                },
                width: "2",
                customAsset: false,
                family: "Website",
                options: {},
                title: translate("dashboard.templates.searchtrafficoverview.widget.ppc.desktop.title"),
            },
        },
        {
            singleModeOnly: true,
            pos: {
                sizeX: 2,
                sizeY: 1,
                maxSizeY: 2,
                minSizeX: 2,
                maxSizeX: 4,
                row: 3,
                col: 2,
            },
            properties: {
                flexibleDuration: true,
                selectedChannel: "Direct",
                metric: "BrandedKeywords",
                webSource: devicesTypes.DESKTOP,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                type: "PieChart",
                filters: {
                    includeSubDomains: "true",
                },
                width: "2",
                customAsset: false,
                family: "Website",
                options: {},
                title: translate("dashboard.templates.searchtrafficoverview.widget.brandedvsnonbranded.desktop.title"),
            },
        },
    ],
});

export default getDashboard;
